"use client";

import { usePathname, useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { Z_OFFER_POPUP } from "@/components/layout/zIndex";
import { useBelowHeader } from "./use-below-header";

const KEY = "gtp-back-to-map";

/** Called by the map's "View →" link: remember which map the visitor left. */
export function rememberMapExit(to: string) {
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ to: to.split(/[?#]/)[0], from: window.location.pathname }));
  } catch {
    /* storage unavailable: no Back to map button */
  }
}

/**
 * "← Back to map" on a page opened from a map's View link (owner, 1 Oct 2026:
 * "after view how can i go back to the same map ... in mobile"). The phone's
 * own Back works too, but an installed iPhone app has none. Going back
 * through history lets the map restore its view, selection and filters
 * (MapAtlas's Back snapshot).
 */
export function BackToMap() {
  const pathname = usePathname();
  const router = useRouter();
  const top = useBelowHeader();
  const [from, setFrom] = useState<string | null>(null);

  useEffect(() => {
    let next: string | null = null;
    try {
      const saved = JSON.parse(sessionStorage.getItem(KEY) ?? "null") as { to: string; from: string } | null;
      if (saved && saved.to === pathname) next = saved.from;
      else if (saved) sessionStorage.removeItem(KEY);
    } catch {
      /* nothing saved */
    }
    const timer = window.setTimeout(() => setFrom(next), 0);
    return () => window.clearTimeout(timer);
  }, [pathname]);

  useEffect(() => {
    const root = document.documentElement;
    if (from) root.setAttribute("data-back-to-map", "");
    else root.removeAttribute("data-back-to-map");
    return () => root.removeAttribute("data-back-to-map");
  }, [from]);

  if (!from) return null;

  const goBack = () => {
    // History first: that is what lets the map come back exactly as it was.
    if (window.history.length > 1) router.back();
    else router.push(from);
  };

  return (
    <button
      type="button"
      onClick={goBack}
      style={{ top: top ?? 0, visibility: top === null ? "hidden" : undefined }}
      className={`fixed left-3 ${Z_OFFER_POPUP} inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-[var(--chrome-border)] bg-[var(--chrome-bg)] px-4 py-2 text-[13px] font-semibold text-[var(--chrome-fg)] shadow-[0_10px_30px_rgba(0,0,0,0.4)] md:left-4`}
    >
      <span aria-hidden>←</span> Back to map
    </button>
  );
}
